/**
 * @description 数据格式化Hook
 * @date 2025-01-17
 * @author lv.chen
 */

import { useMemo } from 'react';

import { formatDateStr } from '@intelligence/v3/utils/date';
import { toDateTypeMomentFormat } from '@intelligence/utils/common';

import type { IPublicFeedback } from '@intelligence/types/topCharts';

const ONCE = 'once';

interface IUseFormattedTableDataParams {
  /** 当前语言环境 */
  locale: string;
  /** 国际化格式化方法 */
  formatMessage: IFormatMessage;
  /** 原始表格数据 */
  tableFeedBackData?: IPublicFeedback[] | null;
}

interface IUseFormattedTableDataReturn {
  /** 格式化后的表格数据 */
  formattedTableData: IPublicFeedback[];
}

/**
 * 格式化指标时间范围
 */
const formatMetricTimeRange = (formatMessage: IFormatMessage, record: IPublicFeedback): string => {
  const { granularity, metric_start_date: metricStartDate, metric_end_date: metricEndDate } = record || {};
  const formatValue = toDateTypeMomentFormat[granularity] || 'YYYY-MM-DD';

  // 一次性数据
  if (metricStartDate === ONCE) {
    return metricEndDate || (formatMessage({ id: 'dbi_feedback_once' }) as string);
  }

  // 空数据
  if (!metricStartDate) {
    return '-';
  }

  // 开始日期和结束日期相同
  if (metricStartDate === metricEndDate) {
    return formatDateStr(metricStartDate, formatValue);
  }

  // 日期范围
  return `${formatDateStr(metricStartDate, formatValue)}~${formatDateStr(metricEndDate, formatValue)}`;
};

/**
 * 数据格式化Hook
 */
export default function useFormattedTableData(params: IUseFormattedTableDataParams): IUseFormattedTableDataReturn {
  const { locale, formatMessage, tableFeedBackData } = params;

  const formattedTableData = useMemo<IPublicFeedback[]>(() => {
    if (!tableFeedBackData) {
      return [];
    }

    return tableFeedBackData.map((item) => {
      return {
        ...item,
        metric_date: formatMetricTimeRange(formatMessage, item),
        fill_time: formatDateStr(item?.fill_time),
      };
    });
  }, [locale, tableFeedBackData]);

  return {
    formattedTableData,
  };
}
